import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Play, Edit2, Trash2, Mail, Clock, ToggleLeft, ToggleRight, Loader2, X, BarChart3, TrendingUp, Users, Target, Beaker, Trophy } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { AdminAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import RichTextEditor from './RichTextEditor';
import { WILAYAS } from '../../data/wilayas';

/**
 * Onglet "Séquences automatiques" — drip campaigns email.
 * Triggers : order_pending · welcome_signup · stale_offers · cancelled_order_recovery
 * Le backend (APScheduler) exécute toutes les séquences activées toutes les 60 min.
 */

const EMPTY = {
  id:                    null,
  name:                  '',
  trigger_type:          'order_pending',
  trigger_days:          3,
  subject:               '',
  body_html:             '',
  enabled:               true,
  audience_role:         '',
  audience_wilaya:       '',
  ab_testing_enabled:    false,
  variant_b_subject:     '',
  variant_b_body_html:   '',
  auto_winner_enabled:   false,
  auto_winner_min_sends: 30,
};

const TRIGGER_LABEL = {
  order_pending:            'Relance commande pending',
  welcome_signup:           'Bienvenue post-inscription',
  stale_offers:             'Offres expirées (fournisseur)',
  cancelled_order_recovery: 'Récupération commande annulée',
};

const SequencesTab = () => {
  const { toast } = useToast();
  const [sequences, setSequences] = useState([]);
  const [triggerTypes, setTriggerTypes] = useState({});
  const [stats, setStats] = useState({ total_sent: 0, last_5: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // EMPTY = create, object = edit
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState({}); // {seqId: bool}

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        AdminAPI.listSequences(),
        AdminAPI.sequencesStats(),
      ]);
      setSequences(list.sequences || []);
      setTriggerTypes(list.trigger_types || {});
      setStats(s || { total_sent: 0, last_5: [] });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les séquences.' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    if (!editing.name.trim() || !editing.subject.trim() || !editing.body_html.trim()) {
      toast({ title: 'Champs manquants', description: 'Nom, sujet et message sont requis.' });
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await AdminAPI.patchSequence(editing.id, {
          name: editing.name, trigger_type: editing.trigger_type, trigger_days: Number(editing.trigger_days),
          subject: editing.subject, body_html: editing.body_html, enabled: editing.enabled,
          audience_role: editing.audience_role || '', audience_wilaya: editing.audience_wilaya || '',
          ab_testing_enabled: !!editing.ab_testing_enabled,
          variant_b_subject: editing.variant_b_subject || '',
          variant_b_body_html: editing.variant_b_body_html || '',
          auto_winner_enabled: !!editing.auto_winner_enabled,
          auto_winner_min_sends: Number(editing.auto_winner_min_sends) || 30,
        });
        toast({ title: '✅ Séquence mise à jour' });
      } else {
        await AdminAPI.createSequence({
          name: editing.name, trigger_type: editing.trigger_type, trigger_days: Number(editing.trigger_days),
          subject: editing.subject, body_html: editing.body_html, enabled: editing.enabled,
          audience_role: editing.audience_role || null, audience_wilaya: editing.audience_wilaya || null,
          ab_testing_enabled: !!editing.ab_testing_enabled,
          variant_b_subject: editing.variant_b_subject || null,
          variant_b_body_html: editing.variant_b_body_html || null,
          auto_winner_enabled: !!editing.auto_winner_enabled,
          auto_winner_min_sends: Number(editing.auto_winner_min_sends) || 30,
        });
        toast({ title: '✅ Séquence créée' });
      }
      setEditing(null);
      await reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setSaving(false); }
  };

  const toggleEnabled = async (seq) => {
    try {
      await AdminAPI.patchSequence(seq.id, { enabled: !seq.enabled });
      setSequences(prev => prev.map(s => s.id === seq.id ? { ...s, enabled: !s.enabled } : s));
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de modifier le statut.' });
    }
  };

  const runNow = async (seq) => {
    setRunning(r => ({ ...r, [seq.id]: true }));
    try {
      const result = await AdminAPI.runSequenceNow(seq.id);
      toast({
        title: '✅ Séquence exécutée',
        description: `${result.matched} cibles · ${result.sent} envoyés · ${result.skipped} ignorés (déjà envoyés) · ${result.failed} échecs`,
      });
      await reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Exécution échouée' });
    } finally { setRunning(r => ({ ...r, [seq.id]: false })); }
  };

  const remove = async (seq) => {
    if (!window.confirm(`Supprimer définitivement la séquence "${seq.name}" ? Le journal des envois sera aussi effacé.`)) return;
    try {
      await AdminAPI.deleteSequence(seq.id);
      toast({ title: '✅ Séquence supprimée' });
      await reload();
    } catch {
      toast({ title: 'Erreur', description: 'Suppression échouée.' });
    }
  };

  // Performance modal
  const [perfSeq, setPerfSeq] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const openPerf = async (seq) => {
    setPerfSeq(seq); setPerfData(null); setPerfLoading(true);
    try {
      const data = await AdminAPI.sequencePerformance(seq.id, 7);
      setPerfData(data);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible de charger la performance.' });
      setPerfSeq(null);
    } finally { setPerfLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12 text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>;

  return (
    <div data-testid="admin-sequences-tab" className="space-y-5">
      {/* Header + stats */}
      <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-medium text-[#2D2A26] flex items-center gap-2"><Mail className="w-4 h-4 text-[#1F4D3F]" /> Séquences email automatiques</h3>
          <p className="text-xs text-[#7A6F5C] mt-1">
            Le backend exécute les séquences activées toutes les 60 minutes (APScheduler).
            Anti-doublon : chaque cible ne reçoit l'email qu'une seule fois par séquence.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-[#7A6F5C] flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4" /> <span><strong className="text-[#1F4D3F]">{stats.total_sent}</strong> envois / 7 derniers jours</span>
          </div>
          <Button data-testid="seq-create-btn" onClick={() => setEditing({ ...EMPTY })} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle séquence
          </Button>
        </div>
      </div>

      {/* Sequence list */}
      {sequences.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8E0D0] rounded-2xl p-10 text-center" data-testid="seq-empty-state">
          <Mail className="w-10 h-10 text-[#C9BFAE] mx-auto mb-3" />
          <p className="text-sm text-[#7A6F5C]">Aucune séquence configurée. Créez votre première séquence pour automatiser les relances email.</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="seq-list">
          {sequences.map(seq => (
            <div key={seq.id} className="bg-white border border-[#E8E0D0] rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid={`seq-row-${seq.id}`}>
              <button type="button" onClick={() => toggleEnabled(seq)} title={seq.enabled ? 'Désactiver' : 'Activer'}
                className={`flex-shrink-0 ${seq.enabled ? 'text-emerald-600' : 'text-[#C9BFAE]'}`}
                data-testid={`seq-toggle-${seq.id}`}>
                {seq.enabled ? <ToggleRight className="w-7 h-7" /> : <ToggleLeft className="w-7 h-7" />}
              </button>
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium text-[#2D2A26]">{seq.name}</div>
                <div className="text-xs text-[#7A6F5C] mt-0.5 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> J+{seq.trigger_days}</span>
                  <span>{TRIGGER_LABEL[seq.trigger_type] || seq.trigger_type}</span>
                  <span className="text-emerald-700">{seq.sent_last_7d || 0} envoyés / 7j</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => openPerf(seq)} title="Voir les performances" data-testid={`seq-perf-${seq.id}`}>
                  <BarChart3 className="w-3.5 h-3.5 text-[#1F4D3F]" />
                </Button>
                <Button variant="outline" size="sm" disabled={running[seq.id]} onClick={() => runNow(seq)} title="Exécuter maintenant" data-testid={`seq-run-${seq.id}`}>
                  {running[seq.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing({ ...seq })} title="Modifier" data-testid={`seq-edit-${seq.id}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => remove(seq)} title="Supprimer" data-testid={`seq-delete-${seq.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent activity */}
      {stats.last_5?.length > 0 && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl p-4" data-testid="seq-recent">
          <h4 className="text-sm font-medium text-[#2D2A26] mb-2">5 derniers envois</h4>
          <ul className="text-xs space-y-1.5 text-[#5A5247]">
            {stats.last_5.map((e) => (
              <li key={`${e.sent_at}-${e.to}`} className="flex items-center justify-between font-mono">
                <span>{e.to} {e.mocked && <span className="text-amber-600 not-italic font-sans normal-case ml-1">(mocked)</span>}</span>
                <span className="text-[#7A6F5C]">{new Date(e.sent_at).toLocaleString('fr-DZ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Edit / create modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto py-6 px-4" data-testid="seq-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E0D0]">
              <h3 className="font-medium text-[#2D2A26]">{editing.id ? 'Modifier la séquence' : 'Nouvelle séquence'}</h3>
              <button onClick={() => setEditing(null)} className="text-[#7A6F5C] hover:text-[#2D2A26]" data-testid="seq-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Nom interne</label>
                <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Ex : Relance commande J+3" data-testid="seq-input-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-sm text-[#2D2A26] mb-1.5 block">Déclencheur</label>
                  <select value={editing.trigger_type} onChange={e => setEditing({ ...editing, trigger_type: e.target.value })}
                    className="w-full h-10 rounded-lg border border-[#E8E0D0] bg-white px-3 text-sm"
                    data-testid="seq-input-trigger">
                    {Object.entries(triggerTypes).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-[#2D2A26] mb-1.5 block">Délai (jours)</label>
                  <Input type="number" min="0" max="365" value={editing.trigger_days}
                    onChange={e => setEditing({ ...editing, trigger_days: e.target.value })}
                    data-testid="seq-input-days" />
                </div>
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Sujet de l'email <span className="text-xs text-[#7A6F5C]">— variables : {'{name}'}, {'{order_id}'}, {'{total}'}, {'{email}'}, {'{company}'}</span></label>
                <Input value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })}
                  placeholder="Ex : Bonjour {name}, votre commande {order_id} attend toujours…"
                  data-testid="seq-input-subject" />
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Message</label>
                <RichTextEditor value={editing.body_html} onChange={(v) => setEditing({ ...editing, body_html: v })}
                  placeholder="Rédigez le message ici. Les variables sont remplacées à l'envoi." testId="seq-input-body" minHeight={200} />
              </div>
              {/* Audience filters (optional) */}
              <div className="bg-[#FAF7F2] border border-[#E8E0D0] rounded-lg p-3 space-y-3">
                <div className="text-xs font-medium text-[#1F4D3F] flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Filtres d'audience (optionnel — laisser vide = toutes les cibles)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#7A6F5C] mb-1 block">Rôle</label>
                    <select value={editing.audience_role || ''} onChange={e => setEditing({ ...editing, audience_role: e.target.value })}
                      className="w-full h-9 rounded-md border border-[#E8E0D0] bg-white px-2 text-sm"
                      data-testid="seq-input-audience-role">
                      <option value="">Tous</option>
                      <option value="buyer">Acheteurs (dentistes)</option>
                      <option value="supplier">Fournisseurs</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#7A6F5C] mb-1 block">Wilaya</label>
                    <select value={editing.audience_wilaya || ''} onChange={e => setEditing({ ...editing, audience_wilaya: e.target.value })}
                      className="w-full h-9 rounded-md border border-[#E8E0D0] bg-white px-2 text-sm"
                      data-testid="seq-input-audience-wilaya">
                      <option value="">Toutes</option>
                      {WILAYAS.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* A/B Testing (optional) */}
              <div className="bg-[#F5F1EA] border border-[#E8E0D0] rounded-lg p-3 space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs font-medium text-[#1F4D3F] flex items-center gap-1.5">
                    <Beaker className="w-3.5 h-3.5" /> A/B Testing — comparer deux variantes (split 50/50 stable)
                  </span>
                  <input type="checkbox" checked={!!editing.ab_testing_enabled}
                    onChange={e => setEditing({ ...editing, ab_testing_enabled: e.target.checked })}
                    className="w-4 h-4" data-testid="seq-input-ab-enabled" />
                </label>
                {editing.ab_testing_enabled && (
                  <div className="space-y-2.5 pt-1">
                    <p className="text-[11px] text-[#7A6F5C]">
                      La variante <strong>A</strong> = le sujet + message principal ci-dessus. La variante <strong>B</strong> ci-dessous sera envoyée à 50% des cibles. Comparez ensuite les taux de conversion via le bouton <em>Performance</em>.
                    </p>
                    <div>
                      <label className="text-xs text-[#7A6F5C] mb-1 block">Sujet — Variante B</label>
                      <Input value={editing.variant_b_subject || ''} onChange={e => setEditing({ ...editing, variant_b_subject: e.target.value })}
                        placeholder="Ex : Bonjour {name}, votre commande {order_id} vous attend"
                        data-testid="seq-input-variant-b-subject" />
                    </div>
                    <div>
                      <label className="text-xs text-[#7A6F5C] mb-1 block">Message — Variante B</label>
                      <RichTextEditor value={editing.variant_b_body_html || ''}
                        onChange={(v) => setEditing({ ...editing, variant_b_body_html: v })}
                        placeholder="Rédigez la variante B…" testId="seq-input-variant-b-body" minHeight={150} />
                    </div>
                    {/* Auto-Winner sub-section */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3">
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
                          <Trophy className="w-3.5 h-3.5" /> Auto-Winner Mode — bascule auto sur la variante gagnante
                        </span>
                        <input type="checkbox" checked={!!editing.auto_winner_enabled}
                          onChange={e => setEditing({ ...editing, auto_winner_enabled: e.target.checked })}
                          className="w-4 h-4 accent-emerald-600" data-testid="seq-input-auto-winner-enabled" />
                      </label>
                      {editing.auto_winner_enabled && (
                        <div className="mt-2.5 space-y-2">
                          <p className="text-[11px] text-emerald-900">
                            Après <strong>{editing.auto_winner_min_sends || 30}</strong> envois par variante, si une variante a un avantage statistique significatif (Z-test, p&lt;0.05), 100% du trafic basculera automatiquement vers elle. Vous ne perdez plus d'envois sur la variante perdante.
                          </p>
                          <div>
                            <label className="text-xs text-[#7A6F5C] mb-1 block">Envois minimum par variante avant évaluation (min. 10)</label>
                            <Input type="number" min="10" max="10000" value={editing.auto_winner_min_sends || 30}
                              onChange={e => setEditing({ ...editing, auto_winner_min_sends: e.target.value })}
                              data-testid="seq-input-auto-winner-min" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-[#2D2A26] cursor-pointer">
                <input type="checkbox" checked={editing.enabled} onChange={e => setEditing({ ...editing, enabled: e.target.checked })}
                  className="w-4 h-4" data-testid="seq-input-enabled" />
                Activer immédiatement (le scheduler enverra dès le prochain tick)
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E8E0D0] bg-[#FAF7F2]">
              <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="seq-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {editing.id ? 'Enregistrer' : 'Créer la séquence'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Performance modal */}
      {perfSeq && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto py-6 px-4" data-testid="seq-perf-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E0D0]">
              <h3 className="font-medium text-[#2D2A26] flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#1F4D3F]" /> Performance — {perfSeq.name}
              </h3>
              <button onClick={() => { setPerfSeq(null); setPerfData(null); }} className="text-[#7A6F5C] hover:text-[#2D2A26]" data-testid="seq-perf-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 max-h-[75vh] overflow-y-auto">
              {perfLoading || !perfData ? (
                <div className="flex items-center justify-center py-16 text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>
              ) : (
                <div className="space-y-5">
                  {/* KPI tiles */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="perf-kpis">
                    <div className="bg-[#FAF7F2] rounded-xl p-3 border border-[#E8E0D0]">
                      <div className="text-[10px] uppercase text-[#7A6F5C] tracking-wide flex items-center gap-1"><Mail className="w-3 h-3" /> Envoyés</div>
                      <div className="text-2xl font-semibold text-[#1F4D3F] mt-1">{perfData.total_sent}</div>
                      <div className="text-[11px] text-[#7A6F5C] mt-0.5">{perfData.real_count} réels · {perfData.mocked_count} mocked</div>
                    </div>
                    <div className="bg-[#FAF7F2] rounded-xl p-3 border border-[#E8E0D0]">
                      <div className="text-[10px] uppercase text-[#7A6F5C] tracking-wide flex items-center gap-1"><Users className="w-3 h-3" /> Cibles uniques</div>
                      <div className="text-2xl font-semibold text-[#1F4D3F] mt-1">{perfData.unique_recipients}</div>
                    </div>
                    <div className="bg-[#FAF7F2] rounded-xl p-3 border border-[#E8E0D0]">
                      <div className="text-[10px] uppercase text-[#7A6F5C] tracking-wide flex items-center gap-1"><Target className="w-3 h-3" /> Conversion (J+{perfData.attribution_window_days})</div>
                      <div className="text-2xl font-semibold text-[#1F4D3F] mt-1">{perfData.conversion_rate_pct}%</div>
                      <div className="text-[11px] text-[#7A6F5C] mt-0.5">{perfData.recipients_with_order} ont commandé</div>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
                      <div className="text-[10px] uppercase text-emerald-700 tracking-wide flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Revenu attribué</div>
                      <div className="text-2xl font-semibold text-emerald-800 mt-1">{new Intl.NumberFormat('fr-DZ').format(Math.round(perfData.attributed_revenue))} DA</div>
                      <div className="text-[11px] text-emerald-700 mt-0.5">{perfData.attributed_orders} commandes</div>
                    </div>
                  </div>

                  {/* Timeline chart */}
                  <div className="bg-white border border-[#E8E0D0] rounded-xl p-3" data-testid="perf-chart">
                    <div className="text-sm font-medium text-[#2D2A26] mb-2 flex items-center gap-1.5">
                      <BarChart3 className="w-4 h-4" /> Envois — 30 derniers jours
                    </div>
                    <div style={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer>
                        <LineChart data={perfData.timeline_30d} margin={{ top: 5, right: 12, left: -8, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0EBE0" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#7A6F5C' }} tickFormatter={(d) => d.slice(5)} />
                          <YAxis tick={{ fontSize: 10, fill: '#7A6F5C' }} allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E8E0D0' }}
                            labelFormatter={(d) => new Date(d).toLocaleDateString('fr-DZ')}
                          />
                          <Line type="monotone" dataKey="count" stroke="#1F4D3F" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* A/B comparison */}
                  {perfData.ab_testing_enabled && perfData.ab_breakdown && (
                    <div className="bg-white border border-[#E8E0D0] rounded-xl p-3" data-testid="perf-ab">
                      <div className="text-sm font-medium text-[#2D2A26] mb-3 flex items-center justify-between gap-1.5">
                        <span className="flex items-center gap-1.5">
                          <Beaker className="w-4 h-4 text-[#1F4D3F]" /> Comparaison A vs B
                        </span>
                        {perfData.auto_winner_locked && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase" data-testid="perf-auto-winner-badge">
                            <Trophy className="w-3 h-3" /> Auto-Winner verrouillé : Variante {perfData.auto_winner_locked.toUpperCase()}
                            {perfData.auto_winner_stats?.p_value !== undefined && <span className="font-normal opacity-80">· p={perfData.auto_winner_stats.p_value}</span>}
                          </span>
                        )}
                        {perfData.auto_winner_enabled && !perfData.auto_winner_locked && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-medium" data-testid="perf-auto-winner-waiting">
                            <Trophy className="w-3 h-3" /> Auto-Winner armé · min {perfData.auto_winner_min_sends}/variante
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {['a', 'b'].map(v => {
                          const data = perfData.ab_breakdown[v] || {};
                          const winning = perfData.ab_breakdown.a && perfData.ab_breakdown.b &&
                            ((v === 'a' && perfData.ab_breakdown.a.conversion_rate > perfData.ab_breakdown.b.conversion_rate) ||
                             (v === 'b' && perfData.ab_breakdown.b.conversion_rate > perfData.ab_breakdown.a.conversion_rate));
                          const locked = perfData.auto_winner_locked === v;
                          return (
                            <div key={v} className={`rounded-xl p-3 border ${locked ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200' : winning ? 'border-emerald-300 bg-emerald-50' : 'border-[#E8E0D0] bg-[#FAF7F2]'}`}
                              data-testid={`perf-ab-${v}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs uppercase font-semibold text-[#7A6F5C]">Variante {v.toUpperCase()}</span>
                                {locked ? <span className="text-[10px] uppercase text-emerald-700 font-bold">🔒 100% trafic</span>
                                  : winning && <span className="text-[10px] uppercase text-emerald-700 font-bold">🏆 Gagnante</span>}
                              </div>
                              <div className="space-y-1.5 text-sm">
                                <div className="flex justify-between"><span className="text-[#7A6F5C]">Envoyés</span><strong className="text-[#2D2A26]">{data.sent || 0}</strong></div>
                                <div className="flex justify-between"><span className="text-[#7A6F5C]">Conversions</span><strong className="text-[#2D2A26]">{data.converted || 0}</strong></div>
                                <div className="flex justify-between"><span className="text-[#7A6F5C]">Taux</span><strong className={winning || locked ? 'text-emerald-700' : 'text-[#2D2A26]'}>{data.conversion_rate || 0}%</strong></div>
                                <div className="flex justify-between"><span className="text-[#7A6F5C]">Revenu</span><strong className="text-[#1F4D3F]">{new Intl.NumberFormat('fr-DZ').format(Math.round(data.revenue || 0))} DA</strong></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-[#7A6F5C]">
                    <strong>Attribution :</strong> commandes passées par le destinataire dans les <strong>{perfData.attribution_window_days} jours</strong> suivant la réception de l'email.
                    Calculé uniquement pour les triggers ciblant les acheteurs (commande pending, bienvenue, récupération annulée).
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SequencesTab;
